import { useCallback } from 'react';
import { graphql } from 'react-relay';

import {
	useLocalActivityCache,
	LocalActivityAction,
} from '@confluence/legacy-schema-bridge/entry-points/useLocalActivityCache.ts';
import type { DehydratedEvent } from '@confluence/legacy-schema-bridge/entry-points/useLocalActivityCache.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import type { useLocalActivityContentFragment$data } from './__generated__/useLocalActivityContentFragment.graphql.ts';

const useLocalActivityContentFragment = graphql`
	fragment useLocalActivityContentFragment on Content {
		id
		title
		hasRestrictions
		hasInheritedRestrictions
		space {
			id
			key
		}
		properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
			nodes {
				id
				value
				key
				version {
					number
				}
			}
		}
	}
`;

const MAX_EVENTS = 100;

export type HydratedEvent = DehydratedEvent & {
	content: useLocalActivityContentFragment$data | DehydratedEvent['content'];
};

export type LocalActivity = {
	viewedEvents: HydratedEvent[];
	deletedContentIds: Set<string>;
	markAsConsistent: (contentId: string) => void;
};

const contentIdsToBeEvicted = new Set<string>();
const isMarkedForEviction = (event: DehydratedEvent) =>
	contentIdsToBeEvicted.has(event.content.id || '');

type LocalActivityFilters = {
	spaceKey?: string;
};

type UseLocalActivityOptions = {
	filters?: LocalActivityFilters;
	skip?: boolean;
};

export const useLocalActivity = ({ filters }: UseLocalActivityOptions = {}): LocalActivity => {
	const readLocalActivityContentFragment =
		useReadFragmentInterop<useLocalActivityContentFragment$data>({
			fragmentSpec: useLocalActivityContentFragment,
		});
	const dehydratedLocalActivities: DehydratedEvent[] = useLocalActivityCache();
	const localActivities: HydratedEvent[] = [];

	for (const event of dehydratedLocalActivities) {
		const hydratedEvent = { ...event };
		try {
			if (event?.content?.id) {
				const contentFragment = readLocalActivityContentFragment({
					id: `Content:${event.content.id}`,
				});
				if (contentFragment) {
					hydratedEvent.content = {
						...hydratedEvent.content,
						...contentFragment,
					};
				}
			}
		} catch {
			// If the fragment does not exist, don't do anything this event will get filtered out
		}
		localActivities.push(hydratedEvent);
	}
	/**
	 * Events which are marked as consistent for all backend APIs can safely be evicted.
	 * At the time of writing, there is only 1 supported backend API (Activity Platform),
	 * so we can immediately mark the event for eviction.
	 *
	 * This eviction is "lazy", meaning that the event is *marked* as evicted & filtered from the
	 * result set, but not actually removed from memory until `trackLocalActivity` is called.
	 */
	const markAsConsistent = useCallback((contentId: string) => {
		contentIdsToBeEvicted.add(contentId);
	}, []);

	const result: LocalActivity = {
		viewedEvents: [],
		deletedContentIds: new Set<string>(),
		markAsConsistent,
	};

	localActivities.forEach((event) => {
		if (!shouldIncludeEvent(event, filters)) {
			return;
		}

		if (event.action === LocalActivityAction.viewed) {
			result.viewedEvents.push(event);
		} else if (event.action === LocalActivityAction.deleted) {
			result.deletedContentIds.add(event.content.id || '');
		}
	});
	return result;
};

const shouldIncludeEvent = (event: HydratedEvent, filters?: LocalActivityFilters) => {
	// Filter events which will be evicted
	if (isMarkedForEviction(event)) {
		return false;
	}

	if (!filters) {
		return true;
	}

	return filters.spaceKey === ('space' in event.content ? event.content.space?.key : undefined);
};

type TrackOptions = {
	contentId: string;
	action: LocalActivityAction;
};

/**
 * In addition to tracking new events, this function is also responsible for:
 * 1. enforcing the MAX_EVENTS cap
 * 2. actually evicting events from cache after they have been marked for eviction
 */
export const trackLocalActivity = (
	{ contentId, action }: TrackOptions,
	setLocalActivity: any,
	getLocalActivity: any,
): void => {
	/**
	 * The Content is dehydrated (i.e. only Content.id is present, not full Content)
	 * because we may not know all Content data at track time.
	 * The full Content will be hydrated from cache automatically when querying in useLocalActivity
	 */
	let dehydratedEvents: DehydratedEvent[] = [];
	try {
		dehydratedEvents =
			getLocalActivity()
				?.filter((e: any) => !isMarkedForEviction(e))
				.slice(0, MAX_EVENTS - 1) || [];
	} catch {}

	contentIdsToBeEvicted.clear();

	const data: { localActivity: DehydratedEvent[] } = {
		localActivity: [
			{
				id: `${contentId}-${action}`,
				action,
				timestamp: new Date().toISOString(),
				content: {
					id: contentId,
					__typename: 'Content',
				},
				__typename: 'LocalActivityEvent',
			},
			...dehydratedEvents,
		],
	};
	setLocalActivity(data.localActivity);
};
